import type {
  ReferralFiltersPayload,
  ReferralSelectedQuestionnaire,
} from 'src/types/referral-v2';
import type {ReferralTrackingDetails} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {DEFAULT_TABLE_PAGE_SIZE} from 'src/utils/referral-v2';

import {
  getAllReferralsDetails,
  getResumeLink,
} from 'src/action-creators/referrer-portal/tracking';

import {getFormattedDate} from 'src/components/referral-v2/common';
import {
  type GenericHeaderItem,
  TOAST_SEMANTIC,
  BasicSingleCell,
  Button,
  BodyMedium,
  Truncate,
  Link,
  Chip,
  Icon,
  toastApi,
  Toast,
  ToastTitle,
  Tooltip,
} from '@spaced-out/ui-design-system/lib/components/index';
import AssignJobPanel from 'src/components/referrer-portal/user-form/assign-job';
import ReferralDetailSidePanel from 'src/components/referral-v2/tracking/tracking-referral-detail';
import AdditionalQuestionModal from 'src/components/referrer-portal/my-referrals/leads/additional-question-modal';
import css from 'src/components/referral-v2/tracking/tracking.css';


export const getHeaders = (
  fileContainer: string,
  filters: ReferralFiltersPayload[],
  t: (arg1: string, arg2: string) => string,
  language?: string,
  referralPayoutEnabled?: boolean,
): GenericHeaderItem<
  ReferralTrackingDetails,
  Record<any, any> /*Extra argument type*/
>[] => {
  const headers = [
    {
      key: 'name',
      label: t('Referral_Name', 'Referral Name'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <ReferralName
          referralDetails={data}
          fileContainer={fileContainer}
          filters={filters}
        />
      ),
      sortable: true,
    },
    {
      key: 'job_title',
      label: t('Job', 'Job'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return <JobTitle filters={filters} data={data} />;
      },
    },
    {
      key: 'agency_name',
      label: t('Organization', 'Organization'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>
            <Truncate
              line={1}
              showFullTextOnHover={true}
              className={css.truncate}
            >
              {data.agency_name ?? '-'}
            </Truncate>
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'program_name',
      label: t('Program_Name', 'Program Name'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>
            <Truncate
              line={1}
              showFullTextOnHover={true}
              className={css.truncate}
            >
              {data.program_name ? data.program_name : '-'}
            </Truncate>
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    ...(referralPayoutEnabled
      ? [
          {
            key: 'bonus_amount',
            label: t('Bonus', 'Bonus'),
            sortable: true,
            // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
            render: ({data}) => (
              <BasicSingleCell className={css.tableCellWidth}>
                <BodyMedium>
                  {data.bonus_amount ? `$${data.bonus_amount}` : '-'}
                </BodyMedium>
              </BasicSingleCell>
            ),
          },
        ]
      : []),
    {
      key: 'requested_on',
      label: t('Submitted_On', 'Submitted On'),
      // @ts-expect-error - TS7031 - Binding element 'requested_on' implicitly has an 'any' type.
      render: ({data: {requested_on}}) => (
        <BasicSingleCell>
          <BodyMedium>
            {requested_on ? getFormattedDate(requested_on, language) : '-'}
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'resume',
      label: t('Resume', 'Resume'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => <Resume resume={data.resume} />,
    },
    {
      key: 'action',
      label: '',
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <ReferCandidate
          jobId={data.job_id}
          filters={filters}
          referralId={data.request_id}
        />
      ),
    },
  ];

  // @ts-expect-error - TS2322 - Type '({ key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable: boolean; } | { key: string; label: string; render: ({ data: { requested_on } }: { data: { requested_on: any; }; }) => Element; sortable?: undefined; })[]' is not assignable to type 'GenericHeaderItem<ReferralTrackingDetails, Record<any, any>>[]'.
  return headers;
};

export const JobTitle = ({
  data,
  filters,
  isPanel,
}: {
  data: ReferralTrackingDetails;
  filters: ReferralFiltersPayload[];
  isPanel?: boolean;
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [jobsPayload, setJobsPayload] = React.useState<any>(null);

  const programQuestions: ReferralSelectedQuestionnaire[] | null | undefined =
    useSelector((state) => state.referrerPortal.program_questions);

  const customQuestions: ReferralSelectedQuestionnaire[] | null | undefined =
    programQuestions?.filter(
      (question: ReferralSelectedQuestionnaire) =>
        question.is_visible &&
        question.response_field.includes('custom_question'),
    );

  // @ts-expect-error - TS2749 - 'jobsPayload' refers to a value, but is being used as a type here. Did you mean 'typeof jobsPayload'?
  const handleAssignJob = (payload: null | jobsPayload) => {
    if (payload && payload.job_id) {
      const body = {
        job_id: payload.job_id,
        job_title: payload.job_title,
        program_id: payload.program_id,
        agency_id: payload.agency_id,
      } as const;
      dispatch(
        reduxApi.put(`request/${data.request_id}/assign-job`, body),
      ).then(() => {
        setIsModalOpen(false);
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {`${t('Job_has_been_assigned_to', 'Job has been assigned to')} `}
              {data.name || t('candidate', 'candidate')}
            </ToastTitle>
          </Toast>,
        );
        dispatch(
          getAllReferralsDetails(
            {
              filters,
              limit: DEFAULT_TABLE_PAGE_SIZE,
              offset: 0,
            },
            true,
          ),
        );
      });
    }
  };

  // @ts-expect-error - TS2749 - 'jobsPayload' refers to a value, but is being used as a type here. Did you mean 'typeof jobsPayload'?
  const handleSelectJob = (payload: jobsPayload) => {
    if (customQuestions && customQuestions.length > 0) {
      setIsModalOpen(true);
      setJobsPayload(payload);
      return;
    }
    handleAssignJob(payload);
  };

  const handleSubmit = (formData: {[key: string]: string}) => {
    dispatch(
      reduxApi.put(`request/${data.request_id}/custom-questions`, formData),
    ).then((response) => {
      if (response) {
        handleAssignJob(jobsPayload);
      }
    });
  };

  return (
    <>
      {isPanel ? (
        <Button onClick={() => setIsOpen(true)}>
          {data.job_title
            ? t('Change_Job', 'Change Job')
            : t('Assign_Job', 'Assign Job')}
        </Button>
      ) : (
        <BasicSingleCell className={css.tableCellWidth}>
          {data.job_title ? (
            <BodyMedium>
              <Truncate
                className={css.truncate}
                line={1}
                showFullTextOnHover={true}
              >
                {data.job_title}
              </Truncate>
            </BodyMedium>
          ) : (
            <Chip
              iconName="plus"
              semantic="secondary"
              onClick={() => setIsOpen(true)}
            >
              {t('Assign_Job', 'Assign Job')}
            </Chip>
          )}
        </BasicSingleCell>
      )}
      {isOpen && (
        <AssignJobPanel
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          handleAssignJob={handleSelectJob}
        />
      )}
      <AdditionalQuestionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customQuestions={customQuestions}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export const ReferCandidate = ({
  referralId,
  jobId,
  filters,
  isPanel,
  onClose,
}: {
  referralId: string;
  jobId: string;
  filters: ReferralFiltersPayload[];
  isPanel?: boolean;
  onClose?: () => unknown;
}) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();

  const handleSubmit = () => {
    dispatch(reduxApi.post(`request/${referralId}`))
      .then(() => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {t(
                'Candidate_referred_successfully',
                'Candidate referred successfully',
              )}
            </ToastTitle>
          </Toast>,
        );
        dispatch(
          getAllReferralsDetails(
            {
              filters,
              limit: DEFAULT_TABLE_PAGE_SIZE,
              offset: 0,
            },
            true,
          ),
        );
        onClose?.();
      })
      .catch((error) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>
              {error.response?.message ||
                t('Internal_server_error', 'Internal server error')}
            </ToastTitle>
          </Toast>,
        );
      });
  };

  const tooltipText = !jobId
    ? t('Assign_Job_to_Refer', 'Assign Job to Refer')
    : t('Refer_Now', 'Refer Now');
  return (
    <BasicSingleCell>
      {isPanel ? (
        <Tooltip title={tooltipText}>
          <span>
            <Button disabled={!jobId} onClick={handleSubmit} type="tertiary">
              {t('Refer_Now', 'Refer Now')}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={tooltipText}>
          <span>
            <Chip
              disabled={!jobId}
              onClick={handleSubmit}
              semantic="information"
            >
              {t('Refer_Now', 'Refer Now')}
            </Chip>
          </span>
        </Tooltip>
      )}
    </BasicSingleCell>
  );
};

const ReferralName = ({
  referralDetails,
  filters,
  fileContainer,
}: {
  referralDetails: ReferralTrackingDetails;
  filters: ReferralFiltersPayload[];
  fileContainer: string;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const {name} = referralDetails;
  return (
    <BasicSingleCell className={css.tableCellWidth}>
      <Link
        as="buttonTextMedium"
        className={css.pointer}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Truncate line={1} showFullTextOnHover={true} className={css.truncate}>
          {name}
        </Truncate>
      </Link>
      {}
      <ReferralDetailSidePanel
        // @ts-expect-error - TS2739 - Type 'ReferralTrackingDetails' is missing the following properties from type 'ReferralTrackingDetails': archive, id
        referralTrackingDetails={referralDetails}
        onClose={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        fileContainer={fileContainer}
        footer={
          <>
            <JobTitle data={referralDetails} filters={filters} isPanel={true} />
            <ReferCandidate
              filters={filters}
              referralId={referralDetails.request_id}
              jobId={referralDetails.job_id}
              isPanel={true}
              onClose={() => setIsOpen(false)}
            />
          </>
        }
      />
    </BasicSingleCell>
  );
};

const Resume = ({resume}: {resume: string}) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();

  if (resume) {
    return (
      <BasicSingleCell>
        <Icon
          className={css.pointer}
          onClick={() => dispatch(getResumeLink(resume))}
          name="download"
        />
      </BasicSingleCell>
    );
  } else {
    return (
      <BasicSingleCell>
        <BodyMedium>{t('NA', 'N/A')}</BodyMedium>
      </BasicSingleCell>
    );
  }
};
